// fallow-ignore-file complexity
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { error as loggerError, log, warn } from '@jepto/shared/utils/logger';
import { events, generateClient, GraphQLResult } from 'aws-amplify/api';
import {
	fetchAuthSessionWithRetry,
	isUnauthenticatedError,
} from '@jepto/shared/utils/fetch-auth-session.util';
import type { EventsChannel } from 'aws-amplify/data';
import { from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { JepCookiesService } from '../jep-cookies/jep-cookies.service';

interface SubscriptionEvent<T> {
	value: GraphQLResult<T>;
	provider?: unknown;
}

interface CachedAccessToken {
	token: string;
	expirationTime: number;
	cachedAt: number;
}

/** Variables object passed to AppSync / Amplify GraphQL operations. */
type GraphQLVariables = Record<string, unknown>;

interface ExtractedGraphQLOperationInfo {
	operationType: string;
	operationName: string;
	variables: GraphQLVariables;
}

@Injectable({
	providedIn: 'root',
})
export class JepRequestService {
	client = generateClient();

	// Secure in-memory cache for accessToken
	private accessTokenCache: CachedAccessToken | null = null;
	private isRefreshingToken = false;
	private refreshTokenPromise: Promise<string> | null = null;
	private readonly TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes before expiration

	/**
	 * Data Chat: SSE + multiple MCP tool calls can run long. Backend may reject tokens that
	 * expire too soon (e.g. "within the next minute"). Ensure at least this much TTL on the
	 * access token before starting a stream; otherwise force refresh.
	 */
	static readonly DATACHAT_MIN_ACCESS_TOKEN_TTL_MS = 60 * 1000;

	constructor(private jepCookieService: JepCookiesService) {}

	async execute(operation, variables = {}, fetchPolicy = 'cache-first') {
		const operationInfo = this.extractOperationInfo(operation, variables);
		const operationName = operationInfo?.operationName ?? 'unnamed';
		const operationType = operationInfo?.operationType ?? 'query';

		return Sentry.startSpan(
			{
				op: 'graphql',
				name: operationName,
				attributes: {
					'graphql.operation': operationName,
					'graphql.type': operationType,
				},
			},
			async () =>
				this.#executeGraphQLRequest(operation, variables, fetchPolicy)
		);
	}

	async #executeGraphQLRequest(
		operation: string,
		variables: GraphQLVariables,
		fetchPolicy: string
	) {
		const isTokenExpired = (error: unknown): boolean => {
			if (
				(error as { code?: string } | null)?.code === 'AuthorizerError'
			) {
				return true;
			}
			const message = String(
				(error as { message?: string } | null)?.message ?? ''
			);
			if (this.messageIndicatesTokenExpired(message)) {
				return true;
			}
			if (this.isGraphQlThrownResult(error)) {
				const first = error.errors[0] as
					| { message?: string; errorType?: string }
					| undefined;
				const nestedMessage = String(first?.message ?? '');
				if (this.messageIndicatesTokenExpired(nestedMessage)) {
					return true;
				}
			}
			return false;
		};

		const executeGraphQL = async (authToken: string) => {
			try {
				const operationInfo = this.extractOperationInfo(
					operation,
					variables
				);
				const result = await this.client.graphql(
					{
						query: operation,
						variables,
						authToken,
					},
					{
						fetchPolicy: fetchPolicy as any,
					}
				);

				log('executeGraphQL: operationInfo', operationInfo);
				log('executeGraphQL: result', result);

				return result;
			} catch (error) {
				loggerError(
					`🚨 [JepRequestService] executeGraphQL failed ${this.safeStringify(
						{
							operationInfo: this.extractOperationInfo(
								operation,
								variables
							),
							fetchPolicy,
							error: this.formatErrorForDebug(error),
						}
					)}`
				);

				if (isTokenExpired(error)) {
					// If token is expired, throw an error to trigger a refresh
					throw new Error('Token expired');
				}
				if (this.isGraphQlThrownResult(error)) {
					// Keep throwing so outer execute() can refresh + retry on Unauthorized.
					if (this.isGraphQlUnauthorizedError(error)) {
						throw error;
					}
					// Otherwise surface as a normal result so callers get full `errors` + `data`
					// (e.g. errorType: 'warning') without a separate mutate() catchError branch.
					return error;
				}
				// If it's a different error, rethrow it (normalize Amplify/fetch empty-body JSON failures)
				throw this.wrapGraphqlTransportErrorIfNeeded(error);
			}
		};

		try {
			let accessToken: string;
			try {
				accessToken = await this.ensureAccessTokenFreshForWrite();
			} catch (tokenError: unknown) {
				if (!isUnauthenticatedError(tokenError)) {
					throw tokenError;
				}
				// Access token missing/expired when the call was made. Attempt one forced
				// refresh (a valid refresh token can still mint a new access token) before
				// giving up, so a borderline-expired session recovers instead of erroring.
				accessToken =
					await this.refreshAccessTokenOrThrowUnauthenticated();
			}
			return await executeGraphQL(accessToken);
		} catch (error) {
			if (!isUnauthenticatedError(error)) {
				console.error(
					'Error[]:',
					this.formatUnknownError(error),
					error
				);
			}
			const isUnauthorizedGraphQL =
				error?.errors?.[0]?.message === 'Unauthorized';
			const isTokenExpiredSentinel =
				error instanceof Error && error.message === 'Token expired';
			// Amplify can throw an unauthenticated error (e.g. NoValidAuthTokens) directly
			// from the GraphQL call when the session lapses mid-request. Treat it like an
			// expired token and try one refresh — but not our own already-converted sentinel.
			const isUnauthenticatedGraphQL =
				isUnauthenticatedError(error) &&
				(error as { name?: string })?.name !== 'NotAuthenticatedError';

			if (
				isUnauthorizedGraphQL ||
				isTokenExpiredSentinel ||
				isUnauthenticatedGraphQL
			) {
				// Force refresh the token
				try {
					const refreshedAccessToken =
						await this.getAccessToken(true);
					return await executeGraphQL(refreshedAccessToken);
				} catch (refreshError: unknown) {
					if (isUnauthenticatedError(refreshError)) {
						const err = new Error('User is not logged in');
						err.name = 'NotAuthenticatedError';
						throw err;
					}
					throw refreshError;
				}
			}
			// If it's not a token expiration error, rethrow it
			throw error;
		}
	}

	/**
	 * Force-refresh the access token, converting an unrecoverable auth failure into a
	 * consistent `NotAuthenticatedError` for callers to handle (e.g. surface a re-auth
	 * prompt). Non-auth refresh failures are rethrown untouched.
	 */
	private async refreshAccessTokenOrThrowUnauthenticated(): Promise<string> {
		try {
			return await this.getAccessToken(true);
		} catch (refreshError: unknown) {
			if (isUnauthenticatedError(refreshError)) {
				throw this.createNotAuthenticatedError(refreshError);
			}
			throw refreshError;
		}
	}

	private createNotAuthenticatedError(cause: unknown): Error {
		const err = new Error('User is not logged in');
		err.name = 'NotAuthenticatedError';
		(err as Error & { cause?: unknown }).cause = cause;
		return err;
	}

	/**
	 * Coerce an unknown value to a GraphQL variables map (object, not array).
	 */
	private normalizeGraphQLVariables(value: unknown): GraphQLVariables {
		if (
			value != null &&
			typeof value === 'object' &&
			!Array.isArray(value)
		) {
			return value as GraphQLVariables;
		}
		return {};
	}

	/**
	 * Extract operation information from GraphQL operation string
	 */
	private extractOperationInfo(
		operation: string,
		variables: unknown
	): ExtractedGraphQLOperationInfo | null {
		if (!operation || typeof operation !== 'string') {
			return null;
		}

		try {
			// Extract operation type and name from the operation string
			const operationMatch = operation.match(
				/(query|mutation|subscription)\s*(\w+)?/
			);
			const operationType = operationMatch ? operationMatch[1] : 'query';

			// Try to extract operation name from the pattern
			let operationName = operationMatch ? operationMatch[2] : null;

			// If no name found in the operation string, try to extract from the first field
			if (!operationName) {
				const fieldMatch = operation.match(/{\s*(\w+)/);
				operationName = fieldMatch ? fieldMatch[1] : 'unnamed';
			}

			return {
				operationType,
				operationName: operationName || 'unnamed',
				variables: this.normalizeGraphQLVariables(variables),
			};
		} catch (error) {
			warn('Failed to extract GraphQL operation info:', error);
			return null;
		}
	}

	private formatErrorForDebug(error: unknown): Record<string, unknown> {
		if (error instanceof Error) {
			const withMeta = error as Error & {
				cause?: unknown;
				errors?: unknown;
				data?: unknown;
			};
			return {
				name: withMeta.name,
				message: withMeta.message,
				stack: withMeta.stack,
				cause: withMeta.cause,
				errors: withMeta.errors,
				data: withMeta.data,
			};
		}
		if (typeof error === 'object' && error !== null) {
			return { value: error };
		}
		return { value: String(error) };
	}

	private safeStringify(value: unknown): string {
		try {
			return JSON.stringify(value);
		} catch {
			return '[unserializable]';
		}
	}

	/**
	 * Surface GraphQL `errors` with an actionable message (operation name, path) and attach
	 * structured details on `error.cause` for Sentry and logs (JEPTO-16X / cryptic messages).
	 */
	private throwFromGraphQLErrors(
		operation: string,
		variables: unknown,
		errors: Array<{ message?: string; path?: unknown }>
	): never {
		const operationInfo = this.extractOperationInfo(operation, variables);
		const first = errors[0];
		const rawMsg = first?.message ?? 'GraphQL error occurred';
		const opName = operationInfo?.operationName ?? 'unnamed';
		const pathPart =
			first?.path != null
				? ` path=${this.safeStringify(first.path)}`
				: '';
		const message = `GraphQL error in ${opName}: ${rawMsg}${pathPart}`;
		const err = new Error(message);
		(err as Error & { cause?: unknown }).cause = {
			graphQLErrors: errors,
			operationInfo,
		};
		throw err;
	}

	/**
	 * Get ID token with automatic refresh handling
	 * Also caches the accessToken when fetched
	 * @param isRefresh - Force refresh the token
	 * @returns Promise<string> - The ID token as a string
	 */
	async getIdToken(isRefresh = false): Promise<string> {
		let session: any;
		try {
			session = await fetchAuthSessionWithRetry({
				forceRefresh: isRefresh,
			});
		} catch (error: unknown) {
			// Do not log when user is simply not logged in (expected on login/signup pages)
			if (!isUnauthenticatedError(error)) {
				loggerError('Failed to fetch Auth Session:', error);
			}
		}
		if (session?.tokens?.idToken) {
			log('getIdToken fetchAuthSession', session);
		}

		// No session or no id token — user is not authenticated
		if (!session?.tokens?.idToken) {
			return '';
		}

		// Cache the accessToken when session is fetched
		if (session?.tokens?.accessToken) {
			this.cacheAccessTokenFromSession(
				session.tokens.accessToken.toString()
			);
		}

		const expiration = session.tokens.idToken.payload?.exp * 1000;
		const currentTime = Date.now();

		// If token is close to expiration (e.g., within 5 minutes), refresh it
		if (expiration - currentTime < this.TOKEN_REFRESH_BUFFER) {
			try {
				const newSession = await fetchAuthSessionWithRetry({
					forceRefresh: true,
				});
				if (newSession.tokens?.idToken) {
					// Cache the accessToken from refreshed session
					if (newSession.tokens?.accessToken) {
						this.cacheAccessTokenFromSession(
							newSession.tokens.accessToken.toString()
						);
					}
					return newSession.tokens.idToken.toString();
				}
			} catch (refreshError: unknown) {
				if (!isUnauthenticatedError(refreshError)) {
					loggerError(
						'Failed to refresh Auth Session:',
						refreshError
					);
				}
			}
			return '';
		}
		return session.tokens.idToken.toString();
	}

	/**
	 * Returns true if the GraphQL response errors indicate unauthenticated (no logged-in user).
	 * Used to avoid logging these as errors when the user is on login/signup or logged out.
	 */
	private isAuthRelatedGraphQLErrors(errors: unknown): boolean {
		if (!Array.isArray(errors) || errors.length === 0) return false;
		const msg = String(
			(errors[0] as { message?: string })?.message ?? ''
		).toLowerCase();
		return (
			msg.includes('unauthorized') ||
			msg.includes('not authorized') ||
			msg.includes('not authenticated') ||
			msg.includes('no current user')
		);
	}

	/**
	 * True when the AWS Amplify HTTP client failed parsing the response body as JSON
	 * (e.g. empty body from a gateway/LB while status was still "successful" enough to read).
	 * @see https://jepto.sentry.io/issues/JEPTO-FF (Unexpected end of JSON input)
	 */
	private isMalformedJsonResponseError(error: unknown): boolean {
		const message = String((error as { message?: string })?.message ?? '');
		if (error instanceof SyntaxError) {
			return (
				message.includes('JSON') ||
				message.includes('Unexpected end of JSON input')
			);
		}
		if (error instanceof TypeError) {
			return message.includes("Failed to execute 'json' on 'Response'");
		}
		return (
			message.includes("Failed to execute 'json' on 'Response'") &&
			message.includes('Unexpected end of JSON input')
		);
	}

	/**
	 * Avoid `new Error(String(obj))` → `"[object Object]"` for GraphQL/network payloads.
	 */
	private formatUnknownError(error: unknown): string {
		if (error instanceof Error) {
			return error.message;
		}
		if (error != null && typeof error === 'object') {
			const o = error as Record<string, unknown>;
			if (typeof o.message === 'string' && o.message.length > 0) {
				return o.message;
			}
			const gqlErrors = o.errors;
			if (Array.isArray(gqlErrors) && gqlErrors.length > 0) {
				const first = gqlErrors[0] as { message?: string };
				if (typeof first?.message === 'string') {
					return first.message;
				}
			}
			try {
				return JSON.stringify(error);
			} catch {
				return Object.prototype.toString.call(error);
			}
		}
		return String(error);
	}

	/**
	 * Replace low-level SyntaxError/TypeError from Response.json() with a stable Error so
	 * RxJS subscribers and stores can handle it like other network failures (no raw parse noise).
	 */
	private wrapGraphqlTransportErrorIfNeeded(error: unknown): Error {
		if (!this.isMalformedJsonResponseError(error)) {
			return error instanceof Error
				? error
				: new Error(this.formatUnknownError(error));
		}
		const wrapped = new Error(
			'The server returned an empty or invalid response. Please try again.'
		);
		wrapped.name = 'GraphQLTransportError';
		(wrapped as Error & { cause?: unknown }).cause = error;
		return wrapped;
	}

	/**
	 * Amplify may throw a GraphQL-shaped value (e.g. partial success with `errors` carrying
	 * `errorType: 'warning'`). Those must not be wrapped or reduced to `{ message }` only.
	 */
	private isGraphQlThrownResult(
		error: unknown
	): error is { errors: unknown[]; data?: unknown } {
		if (typeof error !== 'object' || error === null) {
			return false;
		}
		const errors = (error as { errors?: unknown }).errors;
		return Array.isArray(errors) && errors.length > 0;
	}

	private isGraphQlUnauthorizedError(
		error: unknown
	): error is { errors: Array<{ message?: string }> } {
		if (!this.isGraphQlThrownResult(error)) {
			return false;
		}
		const first = error.errors[0] as { message?: string } | undefined;
		return first?.message === 'Unauthorized';
	}

	/**
	 * Amplify/AppSync expiry messages vary: "Token expired" vs "Token has expired."
	 * Also matches when nested JSON is stringified into `Connection failed: …`.
	 */
	/**
	 * AppSync realtime auth rejection: `Unauthorized`, `Connection failed: UnauthorizedException`
	 * (handshake rejected — Amplify drops the detail) or a nested `Token has expired`.
	 */
	private isAppSyncUnauthorizedError(error: unknown): boolean {
		const texts: string[] = [];
		if (error instanceof Error) {
			texts.push(error.message);
		}
		if (this.isGraphQlThrownResult(error)) {
			const first = error.errors[0] as
				| { message?: string; errorType?: string }
				| undefined;
			texts.push(
				String(first?.message ?? ''),
				String(first?.errorType ?? '')
			);
		}
		return texts.some(
			text =>
				text.toLowerCase().includes('unauthorized') ||
				this.messageIndicatesTokenExpired(text)
		);
	}

	private messageIndicatesTokenExpired(message: string): boolean {
		const lower = message.toLowerCase();
		return (
			lower.includes('token has expired') ||
			lower.includes('token expired')
		);
	}

	/**
	 * Get access token with secure in-memory caching
	 * Caches the token in memory only (not in localStorage/sessionStorage)
	 * Automatically refreshes when expired or close to expiration
	 *
	 * @param forceRefresh - Force refresh the token, bypassing cache
	 * @returns Promise<string> - The access token as a string
	 */
	async getAccessToken(forceRefresh = false): Promise<string> {
		if (this.isRefreshingToken && this.refreshTokenPromise) {
			log('getAccessToken: Waiting for ongoing token refresh', {
				forceRefresh,
			});
			return this.refreshTokenPromise;
		}

		if (forceRefresh) {
			this.clearAccessTokenCache();
			return this.fetchAndCacheAccessToken(true);
		}

		// Check if we have a valid cached token
		if (this.accessTokenCache && this.isTokenValid(this.accessTokenCache)) {
			log('getAccessToken: Using cached token', {
				expiresIn: this.accessTokenCache.expirationTime - Date.now(),
			});
			return this.accessTokenCache.token;
		}

		// Fetch and cache new token — force Cognito refresh if cache existed but was stale
		const hadStaleCache =
			!!this.accessTokenCache &&
			!this.isTokenValid(this.accessTokenCache);
		return this.fetchAndCacheAccessToken(hadStaleCache);
	}

	/**
	 * Access token for Data Chat streaming: same as {@link getAccessToken} but guarantees
	 * the token won't expire within {@link DATACHAT_MIN_ACCESS_TOKEN_TTL_MS} (refreshes if needed).
	 */
	async getAccessTokenForDataChat(): Promise<string> {
		return this.ensureAccessTokenFreshForWrite();
	}

	/**
	 * User-initiated writes: refresh when the cached access token is close to expiry
	 * so AppSync does not reject mid-mutation after an idle tab.
	 */
	async ensureAccessTokenFreshForWrite(): Promise<string> {
		let token = await this.getAccessToken();
		if (!this.accessTokenCache) {
			return token;
		}
		const ttl = this.accessTokenCache.expirationTime - Date.now();
		const needsForceRefresh =
			!this.isTokenValid(this.accessTokenCache) ||
			ttl < JepRequestService.DATACHAT_MIN_ACCESS_TOKEN_TTL_MS;
		if (needsForceRefresh) {
			this.clearAccessTokenCache();
			token = await this.getAccessToken(true);
		}
		return token;
	}

	/**
	 * Cache access token from a session (helper method)
	 * @private
	 */
	private cacheAccessTokenFromSession(accessToken: string): void {
		try {
			// Get expiration from access token payload
			let expirationTime: number;
			try {
				const tokenPayload = this.decodeTokenPayload(accessToken);
				expirationTime = tokenPayload.exp
					? tokenPayload.exp * 1000
					: Date.now() + 60 * 60 * 1000; // Default: 1 hour
			} catch (error) {
				// If decoding fails, use default expiration (1 hour)
				expirationTime = Date.now() + 60 * 60 * 1000;
			}

			// Only cache if we don't have a valid cached token or this one is newer
			if (
				!this.accessTokenCache ||
				!this.isTokenValid(this.accessTokenCache)
			) {
				this.accessTokenCache = {
					token: accessToken,
					expirationTime,
					cachedAt: Date.now(),
				};
				log('cacheAccessTokenFromSession: Token cached', {
					expiresAt: new Date(expirationTime).toISOString(),
				});
			}
		} catch (error) {
			log('cacheAccessTokenFromSession: Error caching token', error);
		}
	}

	/**
	 * Fetch access token from AWS Amplify and cache it securely
	 * @private
	 */
	private async fetchAndCacheAccessToken(
		forceRefresh = false
	): Promise<string> {
		if (this.isRefreshingToken && this.refreshTokenPromise) {
			return this.refreshTokenPromise;
		}

		this.isRefreshingToken = true;

		this.refreshTokenPromise = (async () => {
			try {
				const session = await fetchAuthSessionWithRetry({
					forceRefresh,
				});

				if (!session.tokens?.accessToken) {
					throw new Error('No access token available in session');
				}

				const accessToken = session.tokens.accessToken.toString();

				// Get expiration from access token payload
				// Access tokens typically have 'exp' claim, but we'll use a safe default
				// if not available (1 hour from now)
				let expirationTime: number;
				try {
					// Try to decode the access token to get expiration
					const tokenPayload = this.decodeTokenPayload(accessToken);
					expirationTime = tokenPayload.exp
						? tokenPayload.exp * 1000
						: Date.now() + 60 * 60 * 1000; // Default: 1 hour
				} catch (error) {
					// If decoding fails, use default expiration (1 hour)
					log(
						'getAccessToken: Could not decode token expiration, using default 1 hour'
					);
					expirationTime = Date.now() + 60 * 60 * 1000;
				}

				// Cache the token with expiration time
				this.accessTokenCache = {
					token: accessToken,
					expirationTime,
					cachedAt: Date.now(),
				};

				log('getAccessToken: Token cached successfully', {
					expiresAt: new Date(expirationTime).toISOString(),
					expiresIn: expirationTime - Date.now(),
				});

				return accessToken;
			} catch (error) {
				// Clear cache on error
				this.clearAccessTokenCache();
				log('getAccessToken: Error fetching token', error);
				throw error;
			} finally {
				this.isRefreshingToken = false;
				this.refreshTokenPromise = null;
			}
		})();

		return this.refreshTokenPromise;
	}

	/**
	 * Check if cached token is still valid
	 * Token is valid if it exists and hasn't expired (with buffer time)
	 * @private
	 */
	private isTokenValid(cachedToken: CachedAccessToken): boolean {
		const now = Date.now();
		const timeUntilExpiration = cachedToken.expirationTime - now;

		// Token is valid if it hasn't expired and has more than buffer time remaining
		return timeUntilExpiration > this.TOKEN_REFRESH_BUFFER;
	}

	/**
	 * Decode JWT token payload (simple base64 decode, no verification)
	 * @private
	 */
	private decodeTokenPayload(token: string): any {
		try {
			const base64Url = token.split('.')[1];
			const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			const jsonPayload = decodeURIComponent(
				atob(base64)
					.split('')
					.map(
						c =>
							'%' +
							('00' + c.charCodeAt(0).toString(16)).slice(-2)
					)
					.join('')
			);
			return JSON.parse(jsonPayload);
		} catch (error) {
			log('decodeTokenPayload: Error decoding token', error);
			throw new Error('Invalid token format');
		}
	}

	/**
	 * Clear the access token cache
	 * Useful for logout or when token refresh fails
	 */
	clearAccessTokenCache(): void {
		this.accessTokenCache = null;
		log('getAccessToken: Cache cleared');
	}

	/**
	 * Get cached access token if available and valid
	 * Returns null if no valid cache exists
	 * @returns string | null
	 */
	getCachedAccessToken(): string | null {
		if (this.accessTokenCache && this.isTokenValid(this.accessTokenCache)) {
			return this.accessTokenCache.token;
		}
		return null;
	}

	query({
		query,
		variables = {},
		fetchPolicy = 'cache-first',
	}): Observable<any> {
		return new Observable(observer => {
			from(this.execute(query, variables, fetchPolicy))
				.pipe(
					map((gqlResponse: any) => {
						// Check for GraphQL errors in the response
						if (
							gqlResponse &&
							gqlResponse.errors &&
							gqlResponse.errors.length > 0
						) {
							// Do not log when errors are due to user not being logged in
							if (
								!this.isAuthRelatedGraphQLErrors(
									gqlResponse.errors
								)
							) {
								loggerError(
									`🚨 [JepRequestService] GraphQL errors detected: ${this.safeStringify(
										{
											operationInfo:
												this.extractOperationInfo(
													query,
													variables
												),
											errors: gqlResponse.errors,
										}
									)}`
								);
							}
							this.throwFromGraphQLErrors(
								query,
								variables,
								gqlResponse.errors
							);
						}
						return gqlResponse;
					})
				)
				.subscribe(
					this.createObserverHandlers(observer, {
						onError: error => {
							// Do not log when user is not logged in (expected on login/signup pages)
							if (!isUnauthenticatedError(error)) {
								loggerError(
									`🚨 [JepRequestService] Query error: ${this.safeStringify(
										{
											operationInfo:
												this.extractOperationInfo(
													query,
													variables
												),
											error: this.formatErrorForDebug(
												error
											),
										}
									)}`
								);
							}
						},
					})
				);
		});
	}

	mutate({ mutation, variables = {} }): Observable<any> {
		return new Observable(observer => {
			from(this.execute(mutation, variables))
				.pipe(
					map((gqlResponse: any) => {
						return gqlResponse;
					}),
					catchError((error: unknown) => {
						const message =
							error instanceof Error
								? error.message
								: String(error ?? 'Request failed');
						return of({
							errors: [{ message }],
						});
					})
				)
				.subscribe(this.createObserverHandlers(observer));
		});
	}

	subscribe<T = any>(options: {
		query: string;
		variables?: GraphQLVariables;
	}): Observable<any> {
		return new Observable<any>(observer => {
			let subscription: { unsubscribe: () => void } | undefined;
			let closed = false;
			// One forced refresh per failure; reset once data flows again so a
			// long-lived subscription can recover from later token expiries too.
			let refreshedSinceLastData = false;

			// Extract subscription info for tracking
			const subscriptionInfo = this.extractSubscriptionInfo(
				options.query,
				options.variables
			);
			const channel = this.generateSubscriptionChannel(subscriptionInfo);

			const start = async (forceRefresh: boolean) => {
				try {
					log('Subscription: Initiated', options);
					const accessToken = forceRefresh
						? await this.refreshAccessTokenOrThrowUnauthenticated()
						: await this.getAccessToken();
					const operation = this.client.graphql({
						...options,
						authToken: accessToken,
					});
					const subscriptionResult = await operation;

					if (closed) {
						return;
					}
					if ('subscribe' in subscriptionResult) {
						log('Subscription: Established');

						subscription = subscriptionResult.subscribe({
							next: result => {
								refreshedSinceLastData = false;
								observer.next(result);
							},
							error: error => {
								log('Subscription: Error', error);
								// Realtime handshake rejected (expired/stale token, JEPTO-24Y):
								// Amplify reuses the original authToken, so refresh and resubscribe once.
								const unauthorized =
									this.isAppSyncUnauthorizedError(error);
								if (
									!closed &&
									!refreshedSinceLastData &&
									unauthorized
								) {
									refreshedSinceLastData = true;
									subscription?.unsubscribe();
									void start(true);
									return;
								}
								// Still rejected after a fresh token: report one consistent
								// signed-out signal instead of Amplify's varying shapes.
								observer.error(
									unauthorized
										? this.createNotAuthenticatedError(
												error
											)
										: error
								);
							},
							complete: () => observer.complete(),
						});
					} else {
						log('Subscription: Result', subscriptionResult);
						throw new Error('Subscription setup failed');
					}
				} catch (error) {
					const wrapped =
						this.wrapGraphqlTransportErrorIfNeeded(error);
					log('Subscription: Error', wrapped);
					observer.error(wrapped);
				}
			};

			void start(false);

			// Return an unsubscribe function
			return () => {
				closed = true;
				subscription?.unsubscribe();
			};
		});
	}

	private extractSubscriptionInfo(
		query: string,
		variables: unknown
	): { name: string; variables: GraphQLVariables } {
		// Extract subscription name from GraphQL query
		const match = query.match(/subscription[^{]*{\s*(\w+)/);
		return {
			name: match ? match[1] : 'unknown_subscription',
			variables: this.normalizeGraphQLVariables(variables),
		};
	}

	private generateSubscriptionChannel(subscriptionInfo: {
		name: string;
	}): string {
		// Generate a consistent channel name for tracking
		return `graphql_subscription/${subscriptionInfo.name}`;
	}

	private createObserverHandlers<T>(
		observer: {
			next: (value: T) => void;
			error: (err: unknown) => void;
			complete: () => void;
		},
		hooks?: {
			onNext?: (result: T) => void;
			onError?: (error: unknown) => void;
		}
	) {
		return {
			next: (result: T) => {
				hooks?.onNext?.(result);
				observer.next(result);
			},
			error: (error: unknown) => {
				hooks?.onError?.(error);
				observer.error(error);
			},
			complete: () => {
				observer.complete();
			},
		};
	}

	async getEventsChannel(channel: string): Promise<EventsChannel> {
		const connect = async (
			channel: string,
			accessToken: string
		): Promise<EventsChannel> => {
			return await events.connect(channel, {
				authMode: 'userPool',
				authToken: accessToken,
			});
		};

		try {
			const accessToken = await this.getAccessToken();
			const connection = await connect(channel, accessToken);
			return connection;
		} catch (error) {
			log('Connection: Error', error);

			if (this.isAppSyncUnauthorizedError(error)) {
				const refreshedAccessToken =
					await this.refreshAccessTokenOrThrowUnauthenticated();
				try {
					return await connect(channel, refreshedAccessToken);
				} catch (retryError) {
					throw this.isAppSyncUnauthorizedError(retryError)
						? this.createNotAuthenticatedError(retryError)
						: retryError;
				}
			}

			throw error;
		}
	}
}
